import { ReactNode, useEffect, useState, useMemo } from "react";
import { useStore } from "@/stores/useStore";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import {
  CustomerUpdateForm,
  CustomerUpdateFormValues,
} from "./CustomerUpdateForm";
import { Card, CustomerUpdateStatus, IconNames, Shop } from "@/types/v2";
import { ClassNameValue } from "tailwind-merge";
import { cn } from "@/lib/utils";
import { useLatestCustomerUpdateByCardId } from "@/hooks/queries/useLatestCustomerUpdateByCardId";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import { useCustomerUpdate } from "@/hooks/useCustomerUpdate";
import { Icon } from "@/components/Common/Icon";
import {
  CustomerUpdateUsageLocation,
  useCustomerUpdatesAnalytics,
} from "@/hooks/useCustomerUpdatesAnalytics";
import { AnalyticsLocationProvider } from "@/providers/AnalyticsLocationProvider";

interface CustomerUpdatesDialogProps {
  title?: string;
  buttonTitle?: string;
  buttonIcon?: ReactNode;
  buttonClasses?: ClassNameValue;
  description?: string;
  trigger?: ReactNode;
  externalTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  cardData: Card;
  shop: Shop;
}

export const CustomerUpdatesDialog = ({
  title,
  buttonTitle,
  buttonIcon,
  buttonClasses,
  description,
  trigger,
  externalTrigger,
  open,
  onOpenChange,
  cardData,
  shop,
}: CustomerUpdatesDialogProps) => {
  const [isOpen, setIsOpen] = useState(open ?? false);
  const [submitting, setSubmitting] = useState(false);
  const [create, setCreate] = useState(false);
  const authUser = useStore((s) => s.authUser);
  const serviceAdvisors = useServiceAdvisors();
  const { logSnoozeUsed, logCustomerUpdateCompleted } =
    useCustomerUpdatesAnalytics();

  const authUid = useMemo(() => authUser?.uid ?? undefined, [authUser]);

  const defaultUserId = useMemo(() => {
    const self = serviceAdvisors.find(
      (sa) => sa.shopHeroAuthUid === authUid && !sa.hideInShopHero
    );
    if (self?.shopHeroAuthUid) return self.shopHeroAuthUid;

    const first = serviceAdvisors.find(
      (sa) => !sa.hideInShopHero && !!sa.shopHeroAuthUid
    );
    return first?.shopHeroAuthUid ?? "";
  }, [serviceAdvisors, authUid]);

  // Get the latest customer update for this card
  const {
    data: customerUpdate,
    isLoading: isLoadingCustomerUpdate,
    error: customerUpdateError,
  } = useLatestCustomerUpdateByCardId({
    shopId: shop.id,
    cardId: cardData.id,
    enabled: isOpen, // Only fetch when dialog is open
  });

  // Use the customer update hook for handling submissions and snoozing
  const {
    handleCreateUpdate,
    handleSubmit: handleBaseSubmit,
    handleSnoozeUpdate,
    snoozeTimeMin,
  } = useCustomerUpdate(customerUpdate?.id);

  const createInitialValues = useMemo(
    () => ({
      cardId: cardData.id,
      shopId: shop.id,
      comments: "",
      status: CustomerUpdateStatus.CREATED,
      userId: defaultUserId,
    }),
    [cardData.id, shop.id, defaultUserId]
  );

  // Sync local state with the controlled `open` prop
  useEffect(() => {
    if (typeof open === "boolean") {
      setIsOpen(open);
    }
  }, [open]);

  // Handle the dialog's open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    setCreate(false); // Reset create state when dialog opens/closes
    setSubmitting(false);
    onOpenChange?.(newOpen); // Notify parent if `onOpenChange` is provided
  };

  const handleSave = async (values: CustomerUpdateFormValues) => {
    setSubmitting(true);
    logCustomerUpdateCompleted(
      CustomerUpdateUsageLocation.DIALOG,
      values.userId !== customerUpdate?.userId,
      values.timeUntil
    );
    if (!customerUpdate) {
      try {
        await handleCreateUpdate(cardData.id, values);
        handleOpenChange(false);
      } catch {
        setSubmitting(false);
      }
      return;
    }
    try {
      await handleBaseSubmit(values);
      handleOpenChange(false);
    } catch {
      setSubmitting(false);
    }
  };

  const handleSnooze = async () => {
    setSubmitting(true);
    logSnoozeUsed(CustomerUpdateUsageLocation.DIALOG);
    await handleSnoozeUpdate().finally(() => handleOpenChange(false));
    return;
  };

  return (
    <AnalyticsLocationProvider
      location={CustomerUpdateUsageLocation.DIALOG}
      additionalData={{
        cardId: cardData.id,
        shopId: shop.id,
        customerUpdateId: customerUpdate?.id,
      }}
    >
      <Dialog onOpenChange={handleOpenChange} open={isOpen}>
        {trigger && !externalTrigger ? (
          // Use custom trigger if provided
          <DialogTrigger asChild>{trigger}</DialogTrigger>
        ) : (
          // Default trigger button
          !externalTrigger && (
            <DialogTrigger asChild>
              <Button
                className={cn(buttonClasses)}
                variant={"ghost-primary"}
                onClick={() => handleOpenChange(true)}
              >
                {buttonIcon}
                {buttonTitle ?? "Customer Update"}
              </Button>
            </DialogTrigger>
          )
        )}

        <DialogContent className="max-h-[90vh] max-w-4xl overflow-auto">
          <DialogHeader>
            <DialogTitle>{title ?? "Customer Update"}</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {description ??
              "Schedule or provide a customer update for this job."}
          </DialogDescription>

          {isLoadingCustomerUpdate ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2">
                <Icon
                  name={IconNames.loader}
                  className="h-5 w-5 animate-spin"
                />
                <span>Loading customer update...</span>
              </div>
            </div>
          ) : customerUpdateError ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Icon
                name={IconNames.warning}
                className="mb-2 h-8 w-8 text-destructive"
              />
              <h3 className="mb-1 text-lg font-medium text-destructive">
                Error Loading Customer Update
              </h3>
              <p className="text-sm text-muted-foreground">
                Unable to load customer update information. Please try again.
              </p>
            </div>
          ) : !customerUpdate ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              {!create ? (
                <>
                  <Icon
                    name="bell"
                    className="mb-2 h-8 w-8 text-muted-foreground"
                  />
                  <h3 className="mb-1 text-lg font-medium text-muted-foreground">
                    No Customer Update Available
                  </h3>
                  <p className="mb-4 text-sm text-muted-foreground">
                    There is no customer update available for this card.
                  </p>
                  <Button onClick={() => setCreate(true)}>
                    Create Customer Update
                  </Button>
                </>
              ) : (
                <CustomerUpdateForm
                  key={`create:${cardData.id}`}
                  initialValues={createInitialValues}
                  onSubmit={handleSave}
                  onSnooze={handleSnooze}
                  cardData={cardData}
                  shop={shop}
                  serviceAdvisors={serviceAdvisors}
                  snoozeTime={snoozeTimeMin}
                  submitting={submitting}
                  location={CustomerUpdateUsageLocation.DIALOG}
                />
              )}
            </div>
          ) : (
            <CustomerUpdateForm
              key={customerUpdate.id}
              initialValues={customerUpdate}
              onSubmit={handleSave}
              onSnooze={handleSnooze}
              cardData={cardData}
              shop={shop}
              serviceAdvisors={serviceAdvisors}
              snoozeTime={snoozeTimeMin}
              submitting={submitting}
              location={CustomerUpdateUsageLocation.DIALOG}
            />
          )}
        </DialogContent>
      </Dialog>
    </AnalyticsLocationProvider>
  );
};
